import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Building, Plus, Edit, Trash2, MapPin, Save, Upload, Palette, Briefcase } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// Spec: structured industry-segment dropdown for the tenant settings page.
// Labels are user-facing; values are stable machine identifiers stored on
// the tenant document. Existing tenants without this field show "Not set".
const INDUSTRY_SEGMENTS = [
  { value: 'restaurant',         label: 'Restaurant' },
  { value: 'retail',             label: 'Retail' },
  { value: 'healthcare',         label: 'Healthcare' },
  { value: 'hospitality',        label: 'Hospitality' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'education',          label: 'Education' },
  { value: 'automotive',         label: 'Automotive' },
  { value: 'real_estate',        label: 'Real Estate' },
  { value: 'beauty_wellness',    label: 'Beauty & Wellness' },
  { value: 'travel_tourism',     label: 'Travel & Tourism' },
  { value: 'other',              label: 'Other' },
];
const INDUSTRY_LABEL_BY_VALUE = Object.fromEntries(INDUSTRY_SEGMENTS.map(s => [s.value, s.label]));

export default function Settings() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);
  const [tenantFormData, setTenantFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    industry_segment: ''
  });
  const [regionName, setRegionName] = useState('');
  const [branding, setBranding] = useState(null);
  const [brandingForm, setBrandingForm] = useState({ primary_color: '#1A3E6F', secondary_color: '#5BC4BF' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [savingBranding, setSavingBranding] = useState(false);

  useEffect(() => {
    fetchTenant();
    fetchRegions();
    fetchBranding();
  }, []);

  const fetchTenant = async () => {
    try {
      const response = await fetch(`${API}/tenant`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTenant(data);
        setTenantFormData({
          name: data.name || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          address: data.address || '',
          industry_segment: data.industry_segment || ''
        });
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${API}/regions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRegions(data);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchBranding = async () => {
    try {
      const res = await fetch(`${API}/tenant/branding`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBranding(data.branding);
        if (data.branding) {
          setBrandingForm({
            primary_color: data.branding.primary_color || '#1A3E6F',
            secondary_color: data.branding.secondary_color || '#5BC4BF'
          });
          if (data.branding.logo) setLogoPreview(data.branding.logo);
        }
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      const formData = new FormData();
      formData.append('primary_color', brandingForm.primary_color);
      formData.append('secondary_color', brandingForm.secondary_color);
      if (logoFile) formData.append('logo', logoFile);

      const res = await fetch(`${API}/tenant/branding`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });
      if (res.ok) {
        toast.success('Branding updated');
        fetchBranding();
        setLogoFile(null);
      } else {
        toast.error('Failed to update branding');
      }
    } catch (error) {
      toast.error('Failed to update branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/tenant`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(tenantFormData)
      });

      if (response.ok) {
        toast.success('Organization updated successfully');
        setTenantDialogOpen(false);
        fetchTenant();
      } else {
        toast.error('Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating tenant:', error);
      toast.error('Failed to update organization');
    }
  };

  const handleCreateRegion = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/regions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: regionName })
      });

      if (response.ok) {
        toast.success('Region created successfully');
        setRegionDialogOpen(false);
        setRegionName('');
        fetchRegions();
      } else {
        toast.error('Failed to create region');
      }
    } catch (error) {
      console.error('Error creating region:', error);
      toast.error('Failed to create region');
    }
  };

  const handleDeleteRegion = async (regionId) => {
    if (!confirm('Are you sure you want to delete this region?')) return;
    
    try {
      const response = await fetch(`${API}/regions/${regionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Region deleted');
        fetchRegions();
      } else {
        toast.error('Failed to delete region');
      }
    } catch (error) {
      console.error('Error deleting region:', error);
      toast.error('Failed to delete region');
    }
  };

  if (user?.role !== 'tenant_admin') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">Access Denied</h2>
            <p className="text-[#64748B] mt-2">Only tenant admins can access settings.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-[#64748B] mt-1">
            Manage your organization, regions, and survey configuration
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-[#64748B]">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Organization & Regions Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Organization Settings */}
              <Card className="border-[#E2E8F0] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                    <Building className="w-5 h-5 text-[#1A3E6F]" />
                    Organization
                  </CardTitle>
                  <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-testid="edit-org-btn"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-['Montserrat']">Edit Organization</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleUpdateTenant} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="orgName">Organization Name</Label>
                          <Input
                            id="orgName"
                            value={tenantFormData.name}
                            onChange={(e) => setTenantFormData({ ...tenantFormData, name: e.target.value })}
                            data-testid="org-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contactEmail">Contact Email</Label>
                          <Input
                            id="contactEmail"
                            type="email"
                            value={tenantFormData.contact_email}
                            onChange={(e) => setTenantFormData({ ...tenantFormData, contact_email: e.target.value })}
                            data-testid="org-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contactPhone">Contact Phone</Label>
                          <Input
                            id="contactPhone"
                            value={tenantFormData.contact_phone}
                            onChange={(e) => setTenantFormData({ ...tenantFormData, contact_phone: e.target.value })}
                            data-testid="org-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="address">Address</Label>
                          <Input
                            id="address"
                            value={tenantFormData.address}
                            onChange={(e) => setTenantFormData({ ...tenantFormData, address: e.target.value })}
                            data-testid="org-address-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="industrySegment" className="flex items-center gap-2">
                            <Briefcase className="w-3.5 h-3.5 text-[#64748B]" />
                            Industry Segment
                            <span className="text-xs text-[#94A3B8] font-normal">(optional)</span>
                          </Label>
                          <Select
                            value={tenantFormData.industry_segment || 'unset'}
                            onValueChange={(v) => setTenantFormData({ ...tenantFormData, industry_segment: v === 'unset' ? '' : v })}
                          >
                            <SelectTrigger id="industrySegment" data-testid="org-industry-segment-select">
                              <SelectValue placeholder="Select industry segment…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unset" data-testid="org-industry-segment-option-unset">Not set</SelectItem>
                              {INDUSTRY_SEGMENTS.map(s => (
                                <SelectItem key={s.value} value={s.value} data-testid={`org-industry-segment-option-${s.value}`}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setTenantDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-[#1A3E6F] hover:bg-[#122c50]">
                            Save Changes
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-[#64748B]">Name</p>
                      <p className="font-medium text-[#0F172A]">{tenant?.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Contact Email</p>
                      <p className="font-medium text-[#0F172A]">{tenant?.contact_email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Contact Phone</p>
                      <p className="font-medium text-[#0F172A]">{tenant?.contact_phone || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Address</p>
                      <p className="font-medium text-[#0F172A]">{tenant?.address || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B] flex items-center gap-1.5">
                        <Briefcase className="w-3.5 h-3.5" /> Industry Segment
                      </p>
                      <p className="font-medium text-[#0F172A]" data-testid="org-industry-segment-value">
                        {tenant?.industry_segment
                          ? (INDUSTRY_LABEL_BY_VALUE[tenant.industry_segment] || tenant.industry_segment)
                          : <span className="text-[#94A3B8] italic font-normal">Not set</span>}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Regions */}
              <Card className="border-[#E2E8F0] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#5BC4BF]" />
                    Regions
                  </CardTitle>
                  <Dialog open={regionDialogOpen} onOpenChange={setRegionDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm"
                        className="bg-[#1A3E6F] hover:bg-[#122c50]"
                        data-testid="add-region-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-['Montserrat']">Add Region</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateRegion} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="regionName">Region Name</Label>
                          <Input
                            id="regionName"
                            value={regionName}
                            onChange={(e) => setRegionName(e.target.value)}
                            placeholder="e.g., West Coast, Downtown"
                            required
                            data-testid="region-name-input"
                          />
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setRegionDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-[#1A3E6F] hover:bg-[#122c50]">
                            Create Region
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {regions.length === 0 ? (
                    <div className="text-center py-8">
                      <MapPin className="w-10 h-10 text-[#B4B4B4] mx-auto mb-2" />
                      <p className="text-[#64748B]">No regions defined</p>
                      <p className="text-sm text-[#B4B4B4]">Add regions to organize your branches</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {regions.map((region) => (
                        <div 
                          key={region.region_id}
                          className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg"
                          data-testid={`region-${region.region_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#5BC4BF]/15 rounded flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-[#5BC4BF]" />
                            </div>
                            <span className="font-medium text-[#0F172A]">{region.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRegion(region.region_id)}
                            className="text-[#64748B] hover:text-[#D62B27]"
                            data-testid={`delete-region-${region.region_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Branding */}
            <Card className="border-[#E2E8F0] shadow-sm" data-testid="branding-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#D62B27]" />
                  Branding
                </CardTitle>
                <p className="text-sm text-[#64748B]">Your logo and colors appear on customer surveys alongside the satisFIND branding.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label className="text-[#0F172A] font-medium">Restaurant Logo</Label>
                  <div className="flex items-center gap-6">
                    <div className="w-24 h-24 bg-[#F8F9FA] border-2 border-dashed border-[#E2E8F0] rounded-xl flex items-center justify-center overflow-hidden">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" data-testid="logo-preview" />
                      ) : (
                        <Upload className="w-8 h-8 text-[#B4B4B4]" />
                      )}
                    </div>
                    <div>
                      <label htmlFor="logo-upload" className="cursor-pointer">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E2E8F0] text-sm font-medium text-[#0F172A] hover:bg-[#F8F9FA] transition-colors">
                          <Upload className="w-4 h-4" />
                          {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        </div>
                        <input id="logo-upload" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
                          onChange={handleLogoSelect} data-testid="logo-file-input" />
                      </label>
                      <p className="text-xs text-[#64748B] mt-2">PNG, JPG, SVG or WebP. Max 2MB.</p>
                    </div>
                  </div>
                </div>

                {/* Brand Colors */}
                <div className="space-y-3">
                  <Label className="text-[#0F172A] font-medium">Brand Colors</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-[#64748B]">Primary Color</Label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={brandingForm.primary_color}
                          onChange={e => setBrandingForm(f => ({...f, primary_color: e.target.value}))}
                          className="w-10 h-10 rounded-lg border border-[#E2E8F0] cursor-pointer" data-testid="primary-color-input" />
                        <Input value={brandingForm.primary_color}
                          onChange={e => setBrandingForm(f => ({...f, primary_color: e.target.value}))}
                          className="flex-1 font-mono text-sm" maxLength={7} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-[#64748B]">Secondary Color</Label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={brandingForm.secondary_color}
                          onChange={e => setBrandingForm(f => ({...f, secondary_color: e.target.value}))}
                          className="w-10 h-10 rounded-lg border border-[#E2E8F0] cursor-pointer" data-testid="secondary-color-input" />
                        <Input value={brandingForm.secondary_color}
                          onChange={e => setBrandingForm(f => ({...f, secondary_color: e.target.value}))}
                          className="flex-1 font-mono text-sm" maxLength={7} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-[#F8F9FA] rounded-lg p-4">
                  <p className="text-xs font-medium text-[#64748B] mb-3 uppercase tracking-wide">Survey Header Preview</p>
                  <div className="rounded-lg overflow-hidden" style={{ backgroundColor: brandingForm.primary_color }}>
                    <div className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo" className="h-7 rounded" />
                        ) : (
                          <div className="w-7 h-7 bg-white/10 rounded flex items-center justify-center">
                            <span className="text-white text-xs font-bold font-['Montserrat']">V</span>
                          </div>
                        )}
                        <span className="text-white font-['Montserrat'] font-bold text-sm">{tenant?.name || 'Your Restaurant'}</span>
                      </div>
                      <img src="https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/726h775e_satisfind-notagline.png" alt="satisFIND" className="h-3 brightness-0 invert opacity-40" />
                    </div>
                  </div>
                </div>

                <Button onClick={handleSaveBranding} disabled={savingBranding}
                  className="bg-[#1A3E6F] hover:bg-[#122c50]" data-testid="save-branding-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingBranding ? 'Saving...' : 'Save Branding'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
